import React, { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Loader2, Upload, Link2, ExternalLink, Trash2, FolderOpen, FileText, AlertTriangle } from "lucide-react";

function fmtSize(n) {
  n = Number(n || 0);
  if (!n) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}
function fmtWhen(d) { try { return new Date(d).toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" }); } catch { return ""; } }

export function DriveFiles({ scope, id, canEdit = true, grouped = false }) {
  const base = `/console/${scope === "project" ? "projects" : "tasks"}/${id}/files`;
  const [state, setState] = useState({ files: [], folder_url: null });
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [linking, setLinking] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [uploads, setUploads] = useState([]);
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true); setNotice(null);
    try { const { data } = await api.get(base); setState(data); }
    catch (e) { if (e.response?.status === 400) setNotice(formatApiError(e.response?.data?.detail)); else toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [base]);

  const uploadFiles = async (list) => {
    const files = Array.from(list || []).filter(Boolean);
    if (!files.length) return;
    setUploading(true);
    let ok = 0;
    for (const f of files) {
      const uid = `${f.name}-${Date.now()}-${Math.random()}`;
      setUploads((u) => [...u, { id: uid, name: f.name, pct: 0 }]);
      try {
        const fd = new FormData(); fd.append("file", f);
        await api.post(base, fd, { onUploadProgress: (e) => { const pct = e.total ? Math.round((e.loaded / e.total) * 100) : 0; setUploads((u) => u.map((x) => (x.id === uid ? { ...x, pct } : x))); } });
        ok += 1;
      } catch (err) { toast.error(`${f.name}: ${formatApiError(err.response?.data?.detail)}`); }
      finally { setUploads((u) => u.filter((x) => x.id !== uid)); }
    }
    if (ok) toast.success(`Uploaded ${ok} file${ok !== 1 ? "s" : ""}`);
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
    await load();
  };
  const onPick = async (e) => { await uploadFiles(e.target.files); };
  const onDrop = async (e) => {
    e.preventDefault(); setDragOver(false);
    if (!canEdit || notice) return;
    await uploadFiles(e.dataTransfer?.files);
  };
  const addLink = async () => {
    const url = linkUrl.trim(); if (!url) return;
    setLinking(true);
    try { await api.post(`${base}/link`, { url }); toast.success("File linked"); setLinkUrl(""); await load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLinking(false); }
  };
  const remove = async (fid, name) => {
    if (!window.confirm(`Remove "${name}" from the Drive folder? It will be moved to the Shared Drive trash.`)) return;
    try { await api.delete(`${base}/${fid}`); toast.success("File removed"); await load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const files = state.files || [];
  // group by folder (task) for project view
  const groups = grouped
    ? Object.values(files.reduce((acc, f) => { const k = f.folder || "Project files"; (acc[k] = acc[k] || { name: k, rows: [] }).rows.push(f); return acc; }, {}))
    : [{ name: null, rows: files }];

  return (
    <div data-testid={`drive-files-${scope}`} className="relative"
      onDragOver={(e) => { if (canEdit && !notice) { e.preventDefault(); setDragOver(true); } }}
      onDragLeave={(e) => { if (e.currentTarget === e.target) setDragOver(false); }}
      onDrop={onDrop}>
      {dragOver && canEdit && !notice && (
        <div className="absolute inset-0 z-20 rounded-2xl border-2 border-dashed border-[#723FFF] bg-[#F3EFFF]/85 flex items-center justify-center pointer-events-none" data-testid="drive-dropzone">
          <div className="flex flex-col items-center gap-1.5 text-[#5b2fd6]"><Upload className="h-6 w-6" /><span className="text-sm font-medium">Drop to upload to Drive</span></div>
        </div>
      )}
      {canEdit && !notice && (
        <div className="flex items-center gap-2 flex-wrap mb-3">
          <input ref={fileRef} type="file" multiple onChange={onPick} className="hidden" data-testid="drive-file-input" />
          <Button data-testid="drive-upload-btn" size="sm" onClick={() => fileRef.current?.click()} disabled={uploading} className="bg-[#06060C] text-white rounded-full hover:bg-[#1c1c24]">{uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Upload className="h-4 w-4 mr-1.5" /> Upload file</>}</Button>
          <div className="flex items-center gap-1.5">
            <Input data-testid="drive-link-input" value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addLink(); } }} placeholder="Paste a Google Drive / Docs / Sheets link" className="h-9 w-64 bg-[#F6F6FD] border-[#EEEFEF] text-sm" />
            <Button data-testid="drive-link-btn" size="sm" variant="outline" onClick={addLink} disabled={linking || !linkUrl.trim()} className="border-[#EEEFEF] rounded-full">{linking ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Link2 className="h-4 w-4 mr-1.5" /> Add link</>}</Button>
          </div>
          {state.folder_url && <a data-testid="drive-open-folder" href={state.folder_url} target="_blank" rel="noreferrer" className="ml-auto inline-flex items-center gap-1.5 text-xs font-medium text-[#5b2fd6] hover:text-[#06060C]"><FolderOpen className="h-4 w-4" /> Open in Drive</a>}
        </div>
      )}

      {uploads.length > 0 && (
        <div className="space-y-2 mb-3" data-testid="drive-uploads">
          {uploads.map((u) => (
            <div key={u.id} data-testid="drive-upload-progress" className="rounded-xl border border-[#EEEFEF] bg-white px-3 py-2">
              <div className="flex items-center justify-between text-xs mb-1.5">
                <span className="truncate text-[#06060C] flex items-center gap-1.5"><Loader2 className="h-3 w-3 animate-spin text-[#723FFF] shrink-0" />{u.name}</span>
                <span className="text-[#9FA1A4] shrink-0 ml-2">{u.pct >= 100 ? "Saving to Drive…" : `${u.pct}%`}</span>
              </div>
              <div className="h-1.5 rounded-full bg-[#F3EFFF] overflow-hidden"><div className="h-full bg-[#723FFF] transition-[width] duration-200 ease-out" style={{ width: `${Math.max(u.pct, 4)}%` }} /></div>
            </div>
          ))}
        </div>
      )}

      {notice ? (
        <div className="rounded-2xl bg-[#F59E0B]/[0.08] border border-[#F59E0B]/30 p-4 text-xs text-[#b7791f] flex items-start gap-2" data-testid="drive-files-notice"><AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" /> {notice}</div>
      ) : loading ? (
        <div className="py-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-[#c4c5cc]" /></div>
      ) : files.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[#EEEFEF] bg-[#FCFCFE] py-8 text-center text-sm text-[#9FA1A4]" data-testid="drive-files-empty">No files yet. Drag & drop, upload, or paste a Drive link.</div>
      ) : (
        <div className="space-y-4">
          {groups.map((g) => (
            <div key={g.name || "all"}>
              {g.name && <p className="font-mono-data text-[10px] uppercase tracking-wider text-[#5b2fd6] mb-1.5">{g.name}</p>}
              <div className="rounded-2xl border border-[#EEEFEF] bg-white overflow-hidden divide-y divide-[#f6f6f9]">
                {g.rows.map((f) => (
                  <div key={f.id} data-testid="drive-file-row" className="flex items-center gap-3 px-3 py-2.5 hover:bg-[#FCFCFE] group">
                    {f.iconLink ? <img src={f.iconLink} alt="" className="h-4 w-4 shrink-0" /> : <FileText className="h-4 w-4 text-[#9FA1A4] shrink-0" />}
                    <a href={f.webViewLink} target="_blank" rel="noreferrer" className="text-sm text-[#06060C] hover:text-[#5b2fd6] truncate flex-1 inline-flex items-center gap-1.5">{f.name}<ExternalLink className="h-3 w-3 text-[#c4c5cc] shrink-0" /></a>
                    {f.is_link && <span className="text-[9px] font-mono-data uppercase tracking-wider text-[#5b2fd6] bg-[#F3EFFF] rounded-full px-1.5 py-0.5 shrink-0 inline-flex items-center gap-1"><Link2 className="h-2.5 w-2.5" /> Link</span>}
                    <span className="text-[11px] text-[#9FA1A4] shrink-0 hidden sm:block">{fmtSize(f.size)}</span>
                    <span className="text-[11px] text-[#9FA1A4] shrink-0 hidden md:block">{fmtWhen(f.modifiedTime)}</span>
                    {canEdit && <button data-testid={`drive-file-remove-${f.id}`} onClick={() => remove(f.id, f.name)} title="Remove" className="text-[#c4c5cc] hover:text-rose-600 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"><Trash2 className="h-3.5 w-3.5" /></button>}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
